import { atom } from 'jotai';
import isEqual from 'lodash/isEqual';

import { CameraDetailRecord } from '../types/sidecar';

export type CameraFormState = {
  name: string;
  streamProfile: string;
  streamUrl: string;
  snapshotUrl: string;
  username: string;
  password: string;
};

const initialState = (): CameraFormState => ({
  name: '',
  streamProfile: 'sub',
  streamUrl: '',
  snapshotUrl: '',
  username: '',
  password: '',
});

const mapCameraToForm = (camera: CameraDetailRecord): CameraFormState => ({
  name: camera.name,
  streamProfile: camera.streamProfile ?? 'sub',
  streamUrl: camera.streamUrl ?? '',
  snapshotUrl: camera.snapshotUrl ?? '',
  username: camera.username ?? '',
  password: camera.passwordDecrypted ?? '',
});

export const $cameraForm = atom<CameraFormState>(initialState());
export const $discardCameraBaseline = atom<CameraFormState | null>(null);
export const $lastPopulatedCameraId = atom<string | undefined>(undefined);
export const $isCameraFormPopulated = atom<boolean>(false);

export const $hasUnsavedCameraChanges = atom((get) => {
  const form = get($cameraForm);
  const baseline = get($discardCameraBaseline);

  if (!baseline) return false;

  return !isEqual(form, baseline);
});

export const $canSubmitCameraForm = atom((get) => {
  const form = get($cameraForm);
  if (!form.name.trim()) return false;
  return true;
});

export const $populateCameraFormFromCamera = atom(
  null,
  (_get, set, camera: CameraDetailRecord) => {
    const formState = mapCameraToForm(camera);
    set($cameraForm, formState);
    set($discardCameraBaseline, formState);
    set($lastPopulatedCameraId, camera.id);
    set($isCameraFormPopulated, true);
  },
);

export const $resetCameraFormToCamera = atom(null, (get, set) => {
  const baseline = get($discardCameraBaseline);
  if (baseline) {
    set($cameraForm, baseline);
  }
});

export const $populateCameraFormAction = atom(
  null,
  (get, set, camera: CameraDetailRecord | null | undefined) => {
    if (!camera) return;

    const currentCameraId = camera.id;
    const lastCameraId = get($lastPopulatedCameraId);

    if (currentCameraId !== lastCameraId) {
      set($isCameraFormPopulated, false);
    }

    if (!get($isCameraFormPopulated)) {
      set($populateCameraFormFromCamera, camera);
    }
  },
);

export const $resetCameraForm = atom(null, (_get, set) => {
  set($cameraForm, initialState());
  set($discardCameraBaseline, null);
  set($lastPopulatedCameraId, undefined);
  set($isCameraFormPopulated, false);
});
